import { useState } from "react";
import { Mail, Linkedin, Calendar } from "lucide-react";
import ContactFormDialog from "./ContactFormDialog";

const Contact = () => {
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [bookCallOpen, setBookCallOpen] = useState(false);
  const email = "contact@kayros-global.com";
  return (
    <section id="contact" className="relative py-24 md:py-32 bg-dark-section-bg overflow-hidden">
      {/* Tech Background Elements */}
      
      {/* Fine mesh grid */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--primary) / 0.5) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '24px 24px'
        }}
      />

      {/* Larger blueprint grid */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)
          `,
          backgroundSize: '120px 120px'
        }}
      />

      {/* Diagonal tech lines */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" preserveAspectRatio="none">
        <defs>
          <pattern id="contactDiagonalLines" patternUnits="userSpaceOnUse" width="60" height="60" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="60" stroke="hsl(var(--primary))" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#contactDiagonalLines)" />
      </svg>

      {/* Corner accent - top left */}
      <div className="absolute top-0 left-0 w-40 h-40 opacity-[0.08]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path d="M0 0 L0 40 L10 40 L10 10 L40 10 L40 0 Z" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
          <circle cx="10" cy="10" r="2" fill="hsl(var(--primary))" />
          <line x1="15" y1="10" x2="35" y2="10" stroke="hsl(var(--primary))" strokeWidth="0.5" strokeDasharray="3 2" />
        </svg>
      </div>

      {/* Corner accent - top right */}
      <div className="absolute top-0 right-0 w-40 h-40 opacity-[0.08]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path d="M100 0 L100 40 L90 40 L90 10 L60 10 L60 0 Z" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
          <circle cx="90" cy="10" r="2" fill="hsl(var(--primary))" />
          <line x1="65" y1="10" x2="85" y2="10" stroke="hsl(var(--primary))" strokeWidth="0.5" strokeDasharray="3 2" />
        </svg>
      </div>

      {/* Corner accent - bottom left */}
      <div className="absolute bottom-0 left-0 w-40 h-40 opacity-[0.08]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path d="M0 100 L0 60 L10 60 L10 90 L40 90 L40 100 Z" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
          <circle cx="10" cy="90" r="2" fill="hsl(var(--primary))" />
        </svg>
      </div>

      {/* Corner accent - bottom right */}
      <div className="absolute bottom-0 right-0 w-40 h-40 opacity-[0.08]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path d="M100 100 L100 60 L90 60 L90 90 L60 90 L60 100 Z" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
          <circle cx="90" cy="90" r="2" fill="hsl(var(--primary))" />
        </svg>
      </div>

      {/* Floating data nodes */}
      <div className="absolute top-16 left-[20%] w-2 h-2 rounded-full bg-primary/20" />
      <div className="absolute top-24 right-[15%] w-1.5 h-1.5 rounded-full bg-primary/15" />
      <div className="absolute bottom-20 left-[30%] w-1 h-1 rounded-full bg-primary/10" />
      <div className="absolute bottom-16 right-[25%] w-1.5 h-1.5 rounded-full bg-primary/15" />

      {/* Horizontal tech line */}
      <svg className="absolute top-1/2 left-0 w-full h-px opacity-[0.06]" preserveAspectRatio="none">
        <line x1="0" y1="0" x2="100%" y2="0" stroke="hsl(var(--primary))" strokeWidth="1" strokeDasharray="12 6" />
      </svg>

      {/* Content */}
      <div className="container px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Section header */}
          <h2 className="font-display text-3xl md:text-4xl font-bold text-dark-section-foreground mb-4">
            Let's Discuss Your{" "}
            <span className="text-gradient-gold">Fundraising Goals</span>
          </h2>
          <p className="text-lg text-dark-section-muted mb-12">
            Whether you're preparing for your first institutional round or expanding dual-use capabilities, we can help.
          </p>

          {/* Contact options */}
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 mb-12">
            {emailRevealed ? (
              <a
                href={`mailto:${email}`}
                className="group p-4 sm:p-6 rounded-xl border border-dark-section-border bg-dark-section-card hover:border-primary/30 transition-all duration-300 min-h-[44px]"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <p className="font-medium text-dark-section-foreground mb-1">Email</p>
                <p className="text-sm text-primary break-all">{email}</p>
              </a>
            ) : (
              <button
                onClick={() => setEmailRevealed(true)}
                className="group p-4 sm:p-6 rounded-xl border border-dark-section-border bg-dark-section-card hover:border-primary/30 transition-all duration-300 w-full text-center min-h-[44px]"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <p className="font-medium text-dark-section-foreground mb-1">Email</p>
                <p className="text-sm text-dark-section-muted">Click to reveal</p>
              </button>
            )}

            <a
              href="https://www.linkedin.com/company/kayros-global"
              target="_blank"
              rel="noopener noreferrer"
              className="group p-4 sm:p-6 rounded-xl border border-dark-section-border bg-dark-section-card hover:border-primary/30 transition-all duration-300 min-h-[44px]"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                <Linkedin className="w-6 h-6 text-primary" />
              </div>
              <p className="font-medium text-dark-section-foreground mb-1">LinkedIn</p>
              <p className="text-sm text-dark-section-muted">Connect with us</p>
            </a>

            <button
              type="button"
              onClick={() => setBookCallOpen(true)}
              className="group p-4 sm:p-6 rounded-xl border border-dark-section-border bg-dark-section-card hover:border-primary/30 transition-all duration-300 min-h-[44px] w-full text-center"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <p className="font-medium text-dark-section-foreground mb-1">Book a Call</p>
              <p className="text-sm text-dark-section-muted">Schedule intro meeting</p>
            </button>
            <ContactFormDialog
              hideTrigger
              open={bookCallOpen}
              onOpenChange={setBookCallOpen}
            />
          </div>

          {/* Main CTA */}
          <ContactFormDialog />
        </div>
      </div>
    </section>
  );
};

export default Contact;
